import React, { useState } from "react";
import IamPermissionMatrix from "./IamPermissionMatrix";
import { emptyMatrixFromCatalog } from "./iamPermissionLabels";
import { displayRoleName } from "./iamStudio";
import BrixigoPageLoader from "../../components/common/BrixigoPageLoader";
import {
  ChevronDown,
  ChevronUp,
  Clock,
  Globe,
  GripVertical,
  KeyRound,
  Loader2,
  MapPin,
  MonitorSmartphone,
  MoreHorizontal,
  Plus,
  Search,
  Shield,
  Timer,
} from "lucide-react";

function MiniBarChart({ items, labelKey = "label", valueKey = "count" }) {
  const max = Math.max(...items.map((i) => i[valueKey] || 0), 1);
  if (!items.length) return <p className="iam-empty">No data yet</p>;
  return (
    <div>
      {items.map((item) => (
        <div key={item[labelKey]} className="iam-bar-row">
          <span className="iam-bar-label" title={item[labelKey]}>
            {item[labelKey]}
          </span>
          <div className="iam-bar-track">
            <div
              className="iam-bar-fill"
              style={{ width: `${((item[valueKey] || 0) / max) * 100}%` }}
            />
          </div>
          <span style={{ width: 28, textAlign: "right", fontWeight: 600 }}>{item[valueKey]}</span>
        </div>
      ))}
    </div>
  );
}

export function IamDashboardTab({ dashboard, loading, onSeed, seeding }) {
  if (loading) return <div className="iam-loading"><BrixigoPageLoader compact message="Loading dashboard…" fullPage={false} /></div>;
  if (!dashboard) return <div className="iam-empty">Dashboard unavailable</div>;

  const overview = [
    { label: "Total Users", value: dashboard.total_users },
    { label: "Active Users", value: dashboard.active_users, cls: "ok" },
    { label: "Inactive Users", value: dashboard.inactive_users },
    { label: "Pending Invitations", value: dashboard.pending_invitations, cls: "warn" },
    { label: "Super Admins", value: dashboard.admin_users },
    { label: "Payroll Users", value: dashboard.payroll_users },
    { label: "HR Users", value: dashboard.hr_users },
    { label: "Managers", value: dashboard.managers },
    { label: "Approvers", value: dashboard.approvers },
    { label: "Custom Roles", value: dashboard.custom_roles },
  ];

  const security = [
    { label: "Failed Logins (7d)", value: dashboard.failed_login_attempts, cls: "danger" },
    { label: "Locked Accounts", value: dashboard.locked_accounts, cls: "danger" },
    { label: "Expired Invitations", value: dashboard.expired_invitations, cls: "warn" },
    { label: "Full Access Users", value: dashboard.users_with_full_access },
    { label: "Without MFA", value: dashboard.users_without_mfa, cls: "warn" },
    { label: "Inactive Accounts", value: dashboard.inactive_accounts },
  ];

  const highlights = [
    { label: "Total users", value: dashboard.total_users },
    { label: "Active", value: dashboard.active_users, cls: "ok" },
    { label: "Inactive", value: dashboard.inactive_users },
    { label: "Roles", value: dashboard.custom_roles },
  ];

  return (
    <div className="iam-ux">
      <header className="iam-ux-head">
        <div>
          <h2>Dashboard</h2>
          <p>Who can sign in, which roles exist, and what changed recently.</p>
        </div>
        <button type="button" className="iam-ux-add" onClick={onSeed} disabled={seeding}>
          {seeding ? <Loader2 size={16} className="animate-spin" /> : <Shield size={16} />}
          Add standard roles
        </button>
      </header>
      <div className="iam-ux-stats">
        {highlights.map((card) => (
          <article key={card.label} className={`iam-ux-stat ${card.cls || ""}`}>
            <span>{card.label}</span>
            <strong>{card.value ?? 0}</strong>
          </article>
        ))}
      </div>
      <h3 className="iam-ux-section-label">People</h3>
      <div className="iam-ux-stats compact">
        {overview.filter((card) => !["Total Users", "Active Users", "Custom Roles"].includes(card.label)).map((card) => (
          <article key={card.label} className={`iam-ux-stat ${card.cls || ""}`}>
            <span>{card.label}</span>
            <strong>{card.value ?? 0}</strong>
          </article>
        ))}
      </div>
      <h3 className="iam-ux-section-label">Security</h3>
      <div className="iam-ux-stats compact">
        {security.map((card) => (
          <article key={card.label} className={`iam-ux-stat ${card.cls || ""}`}>
            <span>{card.label}</span>
            <strong>{card.value ?? 0}</strong>
          </article>
        ))}
      </div>
      <div className="iam-ux-grid">
        <section className="iam-ux-card pad">
          <h3>Users by role</h3>
          <MiniBarChart
            items={(dashboard.users_by_role || []).map((item) => ({
              ...item,
              label: displayRoleName(item.label),
            }))}
          />
        </section>
        <section className="iam-ux-card pad">
          <h3>Users by department</h3>
          <MiniBarChart items={dashboard.users_by_department || []} />
        </section>
        <section className="iam-ux-card pad">
          <h3>Users by branch</h3>
          <MiniBarChart items={dashboard.users_by_branch || []} />
        </section>
        <section className="iam-ux-card pad">
          <h3>Permissions</h3>
          <MiniBarChart items={dashboard.permission_distribution || []} />
        </section>
      </div>
      <section className="iam-ux-card pad">
        <h3>Recent access activity</h3>
        {(dashboard.recent_activities || []).map((activity, index) => (
          <div key={index} className="iam-ux-activity">
            <strong>{activity.action}</strong>
            <span>{[activity.module, activity.at].filter(Boolean).join(" · ")}</span>
          </div>
        ))}
        {!dashboard.recent_activities?.length && (
          <div className="iam-ux-zero slim">
            <strong>No activity yet</strong>
            <p>Role changes, sign-ins, and password resets will show up here.</p>
          </div>
        )}
      </section>
    </div>
  );
}

export function IamUsersTab({
  users,
  loading,
  filters,
  onFilterChange,
  onInvite,
  onInviteEmployee,
  onAssignRoles,
  canManageAccess,
  onBulk,
  selected,
  onSelect,
}) {
  const [menuRow, setMenuRow] = useState(null);

  const withLogin = users.filter((u) => u.has_login).length;
  const noLogin = users.filter((u) => !u.has_login).length;
  const loginUserIds = users.filter((u) => u.user_id).map((u) => u.user_id);

  if (loading) return <div className="iam-loading"><BrixigoPageLoader compact fullPage={false} /></div>;

  const accessBadge = (u) => {
    const st = u.access_status || (u.has_login ? u.status : "no_access");
    const labelMap = {
      no_access: "No login",
      invited: "Invited",
      pending: "Pending invite",
      active: "Active",
      inactive: "Inactive",
      suspended: "Suspended",
    };
    const label = labelMap[st] || st;
    const cls = st === "pending" ? "pending" : st;
    return <span className={`iam-badge ${cls}`}>{label}</span>;
  };

  return (
    <>
      <p className="iam-users-summary">
        Showing <strong>{users.length}</strong> people —{" "}
        <strong>{withLogin}</strong> with login, <strong>{noLogin}</strong> employee-only (invite to grant access).
      </p>
      <div className="iam-toolbar">
        <input
          className="iam-search"
          placeholder="Search name, email, employee ID…"
          value={filters.search || ""}
          onChange={(e) => onFilterChange({ search: e.target.value })}
        />
        <select
          className="iam-select"
          value={filters.access || ""}
          onChange={(e) => onFilterChange({ access: e.target.value || undefined })}
        >
          <option value="">All people</option>
          <option value="login">Has login</option>
          <option value="no_login">No login yet</option>
        </select>
        <select
          className="iam-select"
          value={filters.status || ""}
          onChange={(e) => onFilterChange({ status: e.target.value || undefined })}
        >
          <option value="">All access status</option>
          <option value="active">Active login</option>
          <option value="no_access">No access</option>
          <option value="invited">Invited</option>
          <option value="inactive">Inactive</option>
          <option value="suspended">Suspended</option>
        </select>
        <select
          className="iam-select"
          value={filters.role || ""}
          onChange={(e) => onFilterChange({ role: e.target.value || undefined })}
        >
          <option value="">All roles</option>
          <option value="admin">Super Admin</option>
          <option value="hr">HR</option>
          <option value="finance">Finance</option>
          <option value="employee">Employee</option>
        </select>
        <select
          className="iam-select"
          value={filters.mfa_enabled === undefined ? "" : String(filters.mfa_enabled)}
          onChange={(e) => {
            const v = e.target.value;
            onFilterChange({ mfa_enabled: v === "" ? undefined : v === "true" });
          }}
        >
          <option value="">MFA any</option>
          <option value="true">MFA on</option>
          <option value="false">MFA off</option>
        </select>
        <button type="button" className="iam-btn primary" onClick={onInvite}>
          Invite user
        </button>
        {selected.length > 0 && (
          <>
            <button type="button" className="iam-btn" onClick={() => onBulk("activate")}>Activate</button>
            <button type="button" className="iam-btn" onClick={() => onBulk("deactivate")}>Deactivate</button>
            <button type="button" className="iam-btn danger" onClick={() => onBulk("delete")}>Delete</button>
          </>
        )}
      </div>
      <div className="iam-table-wrap">
        <table className="iam-table iam-table-users">
          <thead>
            <tr>
              <th className="col-check">
                <input
                  type="checkbox"
                  aria-label="Select all with login"
                  onChange={(e) => onSelect(e.target.checked ? loginUserIds : [])}
                />
              </th>
              <th className="col-user">Name / email</th>
              <th className="col-id">Employee ID</th>
              <th className="col-role">Role</th>
              <th className="col-dept">Department</th>
              <th className="col-branch">Branch</th>
              <th className="col-access">System access</th>
              <th className="col-emp">Employee</th>
              <th className="col-mfa">MFA</th>
              <th className="col-login">Last login</th>
              <th className="col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.row_id}>
                <td className="col-check">
                  {u.user_id ? (
                    <input
                      type="checkbox"
                      checked={selected.includes(u.user_id)}
                      onChange={(e) => {
                        if (e.target.checked) onSelect([...selected, u.user_id]);
                        else onSelect(selected.filter((id) => id !== u.user_id));
                      }}
                    />
                  ) : null}
                </td>
                <td className="col-user">
                  <div className="iam-user-cell">
                    <span className="iam-avatar">
                      {(u.full_name || u.email || u.work_email || "?")[0].toUpperCase()}
                    </span>
                    <div className="iam-user-cell-text">
                      <div className="name" title={u.full_name}>{u.full_name || "—"}</div>
                      <div className="email" title={u.email || u.work_email}>
                        {u.email || u.work_email || "—"}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="col-id">{u.employee_code || "—"}</td>
                <td className="col-role">{displayRoleName((u.roles || [])[0] || u.role || "—")}</td>
                <td className="col-dept">{u.department || "—"}</td>
                <td className="col-branch">{u.branch || "—"}</td>
                <td className="col-access">{accessBadge(u)}</td>
                <td className="col-emp">
                  <span className={`iam-badge ${u.employee_status === "active" ? "active" : "inactive"}`}>
                    {u.employee_status || "—"}
                  </span>
                </td>
                <td className="col-mfa">{u.has_login ? (u.mfa_enabled ? "Yes" : "No") : "—"}</td>
                <td className="col-login">
                  {u.last_login ? new Date(u.last_login).toLocaleDateString() : "—"}
                </td>
                <td className="col-actions" style={{ position: "relative" }}>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4, justifyContent: "flex-end" }}>
                    {canManageAccess && (
                      <button
                        type="button"
                        className="iam-btn primary"
                        style={{ padding: "0.35rem 0.55rem", fontSize: "0.7rem" }}
                        onClick={() => onAssignRoles?.(u)}
                        title="Assign or change roles (admin)"
                      >
                        Roles
                      </button>
                    )}
                    {!u.has_login ? (
                      <button
                        type="button"
                        className="iam-btn"
                        style={{ padding: "0.35rem 0.5rem", fontSize: "0.7rem" }}
                        onClick={() => onInviteEmployee?.(u)}
                      >
                        Invite
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="iam-btn"
                        style={{ padding: "0.35rem 0.4rem" }}
                        onClick={() => setMenuRow(menuRow === u.row_id ? null : u.row_id)}
                      >
                        <MoreHorizontal size={14} />
                      </button>
                    )}
                  </div>
                  {menuRow === u.row_id && u.has_login && (
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        top: "100%",
                        background: "var(--iam-card)",
                        border: "1px solid var(--iam-border)",
                        borderRadius: 8,
                        padding: 4,
                        zIndex: 10,
                        minWidth: 160,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      }}
                    >
                      {canManageAccess && (
                        <button
                          type="button"
                          className="iam-btn"
                          style={{ display: "block", width: "100%", textAlign: "left" }}
                          onClick={() => {
                            setMenuRow(null);
                            onAssignRoles?.(u);
                          }}
                        >
                          Assign roles
                        </button>
                      )}
                      {["View profile", "Reset password", "Deactivate"].map((a) => (
                        <button
                          key={a}
                          type="button"
                          className="iam-btn"
                          style={{ display: "block", width: "100%", textAlign: "left" }}
                        >
                          {a}
                        </button>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!users.length && <p className="iam-empty">No people found. Add employees in Employee List first.</p>}
      </div>
    </>
  );
}

export function IamRolesTab({ roles, catalog, loading, onSaveRole, onHierarchySave, saving }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "",
    description: "",
    role_category: "custom",
    data_access_scope: "reporting_employees",
    permission_matrix: {},
  });

  const openRoleForm = (roleOrNew) => {
    if (roleOrNew === "new") {
      setEditing("new");
      setForm({
        name: "",
        description: "",
        role_category: "custom",
        data_access_scope: "reporting_employees",
        permission_matrix: catalog ? emptyMatrixFromCatalog(catalog) : {},
      });
      return;
    }
    const r = typeof roleOrNew === "object" ? roleOrNew : roles.find((x) => x.role_id === roleOrNew);
    setEditing(r?.role_id || roleOrNew);
    setForm({
      name: r?.name || "",
      description: r?.description || "",
      role_category: r?.role_category || "custom",
      data_access_scope: r?.data_access_scope || "reporting_employees",
      permission_matrix: r?.permission_matrix || (catalog ? emptyMatrixFromCatalog(catalog) : {}),
      role_color: r?.role_color,
    });
  };
  const [hierarchy, setHierarchy] = useState([]);

  React.useEffect(() => {
    setHierarchy(
      [...roles].sort((a, b) => (b.priority_level || 0) - (a.priority_level || 0)).map((r) => ({
        role_id: r.role_id,
        name: r.name,
        parent_role_id: r.parent_role_id,
        role_color: r.role_color,
      }))
    );
  }, [roles]);

  const moveItem = (idx, dir) => {
    const next = [...hierarchy];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setHierarchy(next);
  };

  if (loading) return <div className="iam-loading"><BrixigoPageLoader compact fullPage={false} /></div>;

  return (
    <>
      <div className="iam-toolbar">
        <button type="button" className="iam-btn primary" onClick={() => openRoleForm("new")}>
          Create role
        </button>
        <button type="button" className="iam-btn" onClick={() => onHierarchySave(hierarchy)} disabled={saving}>
          Save hierarchy
        </button>
      </div>
      <div className="iam-panel">
        <div className="iam-panel-title">Role hierarchy (drag order)</div>
        <ul className="iam-hierarchy-list">
          {hierarchy.map((r, idx) => (
            <li key={r.role_id} className="iam-hierarchy-item">
              <GripVertical size={14} style={{ opacity: 0.4 }} />
              <span style={{ width: 12, height: 12, borderRadius: 4, background: r.role_color || "#6366f1" }} />
              <span style={{ flex: 1, fontWeight: 600 }}>{r.name}</span>
              <button
                type="button"
                className="iam-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  moveItem(idx, -1);
                }}
              >
                <ChevronUp size={14} />
              </button>
              <button
                type="button"
                className="iam-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  moveItem(idx, 1);
                }}
              >
                <ChevronDown size={14} />
              </button>
              <button
                type="button"
                className="iam-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  openRoleForm(r);
                }}
              >
                Edit permissions
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className="iam-table-wrap">
        <table className="iam-table">
          <thead>
            <tr>
              <th>Role</th>
              <th>Category</th>
              <th>Priority</th>
              <th>Data scope</th>
              <th>Users</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {roles.map((r) => (
              <tr
                key={r.role_id}
                style={{ cursor: "pointer" }}
                onClick={() => openRoleForm(r)}
              >
                <td>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: r.role_color }} />
                    <strong>{r.name}</strong>
                  </span>
                </td>
                <td>{r.role_category}</td>
                <td>{r.priority_level}</td>
                <td>{r.data_access_scope}</td>
                <td>{r.assigned_user_count}</td>
                <td><span className={`iam-badge ${r.status === "active" ? "active" : "inactive"}`}>{r.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && catalog && (
        <div className="iam-modal-backdrop" onClick={() => setEditing(null)}>
          <div className="iam-modal iam-modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="iam-modal-header">
              <h3>{editing === "new" ? "New role" : `Edit role — ${form.name || ""}`}</h3>
              <button type="button" className="iam-btn" onClick={() => setEditing(null)}>Close</button>
            </div>
            <div className="iam-modal-body">
              <div className="iam-form-grid">
                <div className="iam-field">
                  <label>Role name</label>
                  <input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
                </div>
                <div className="iam-field">
                  <label>Category</label>
                  <select value={form.role_category} onChange={(e) => setForm((p) => ({ ...p, role_category: e.target.value }))}>
                    {["system", "admin", "hr", "payroll", "finance", "manager", "operations", "approver", "compliance", "employee", "custom"].map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="iam-field">
                  <label>Data access scope</label>
                  <select value={form.data_access_scope} onChange={(e) => setForm((p) => ({ ...p, data_access_scope: e.target.value }))}>
                    {(catalog.data_access_scopes || []).map((s) => (
                      <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="iam-field" style={{ marginTop: "0.75rem" }}>
                <label>Description</label>
                <textarea rows={2} value={form.description || ""} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
              </div>

              <p className="iam-panel-title" style={{ marginTop: "1.25rem" }}>
                Permissions (like Zoho Payroll)
              </p>
              <p style={{ fontSize: "0.75rem", color: "var(--iam-muted)", marginBottom: "0.5rem" }}>
                Toggle each module, then set View / Create / Edit / Approve per row. Saved to{" "}
                <code>roles.permission_matrix</code> in the database.
              </p>
              <IamPermissionMatrix
                catalog={catalog}
                matrix={form.permission_matrix || {}}
                onChange={(permission_matrix) => setForm((p) => ({ ...p, permission_matrix }))}
              />

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: "1.25rem" }}>
                <button type="button" className="iam-btn" onClick={() => setEditing(null)}>Cancel</button>
                <button
                  type="button"
                  className="iam-btn primary iam-sticky-save"
                  style={{ position: "static", boxShadow: "none" }}
                  disabled={saving}
                  onClick={() => onSaveRole(editing === "new" ? null : editing, form)}
                >
                  {saving ? "Saving…" : "Save role & permissions"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function IamPermissionTemplatesTab({ groups, catalog, onCreate }) {
  const [name, setName] = useState("");
  return (
    <>
      <div className="iam-toolbar">
        <input className="iam-search" placeholder="New template name" value={name} onChange={(e) => setName(e.target.value)} />
        <button type="button" className="iam-btn primary" onClick={() => { onCreate({ name, permissions: [], is_template: true }); setName(""); }}>
          Create template
        </button>
      </div>
      <div className="iam-panel">
        <div className="iam-panel-title">Available actions</div>
        <p style={{ fontSize: "0.8rem", color: "var(--iam-muted)", marginBottom: "0.75rem" }}>
          {(catalog?.actions || []).join(" · ")}
        </p>
      </div>
      {groups.map((g) => (
        <div key={g.group_id} className="iam-panel">
          <strong>{g.name}</strong>
          <p style={{ fontSize: "0.75rem", color: "var(--iam-muted)" }}>{g.description || "Permission group"}</p>
          <p style={{ fontSize: "0.7rem", marginTop: 4 }}>{(g.permissions || []).length} permission rules</p>
        </div>
      ))}
      {!groups.length && <p className="iam-empty">No permission templates yet. Create one to reuse across roles.</p>}
    </>
  );
}

export function IamWorkflowsTab({ workflows, onCreate }) {
  const [name, setName] = useState("");
  const [moduleKey, setModuleKey] = useState("leave");
  const [error, setError] = useState("");

  const submit = () => {
    if (!name.trim()) {
      setError("Give the workflow a name, for example Leave approval.");
      return;
    }
    setError("");
    onCreate({
      name: name.trim(),
      module_key: moduleKey,
      workflow_type: "sequential",
      levels: [{ level_order: 1, label: "Manager" }, { level_order: 2, label: "HR" }],
    });
    setName("");
  };

  const moduleLabel = { leave: "Leave", attendance: "Attendance", payroll: "Payroll" };

  return (
    <div className="iam-ux">
      <header className="iam-ux-head">
        <div>
          <h2>Approval Workflows</h2>
          <p>Set the order a request must follow. The first person approves, then the next.</p>
        </div>
        <span className="iam-ux-count">{workflows.length} {workflows.length === 1 ? "workflow" : "workflows"}</span>
      </header>

      <div className="iam-ux-guide">
        <article>
          <span>1</span>
          <div>
            <strong>Name the path</strong>
            <p>Use a name people recognise, such as Leave approval.</p>
          </div>
        </article>
        <article>
          <span>2</span>
          <div>
            <strong>Pick the module</strong>
            <p>Leave, Attendance, or Payroll. One path belongs to one module.</p>
          </div>
        </article>
        <article>
          <span>3</span>
          <div>
            <strong>Manager, then HR</strong>
            <p>Every new path uses those two steps, in that order. HR acts only after the manager approves.</p>
          </div>
        </article>
      </div>

      <section className="iam-ux-card pad">
        <h3>Add a path</h3>
        <p className="iam-ux-note">Type a name, choose where it applies, then save. The steps are fixed: Manager first, HR second.</p>
        <div className="iam-ux-formrow">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Workflow name, for example Leave approval"
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          />
          <select value={moduleKey} onChange={(e) => setModuleKey(e.target.value)} aria-label="Module">
            <option value="leave">Leave</option>
            <option value="attendance">Attendance</option>
            <option value="payroll">Payroll</option>
          </select>
          <button type="button" className="iam-ux-add" onClick={submit}>
            <Plus size={16} /> Add workflow
          </button>
        </div>
        {error && <p className="iam-studio-error">{error}</p>}
      </section>

      {workflows.map((workflow) => (
        <article key={workflow.workflow_id} className="iam-ux-flow">
          <div>
            <strong>{workflow.name}</strong>
            <span>{moduleLabel[workflow.module_key] || workflow.module_key}</span>
          </div>
          <div className="iam-ux-steps">
            {(workflow.levels || []).map((level, index) => (
              <span key={level.level_id || level.level_order}>
                {index > 0 && <em>then</em>}
                {index + 1}. {level.label || `Level ${level.level_order}`}
              </span>
            ))}
          </div>
          <span className="iam-ux-status on"><i />One after another</span>
        </article>
      ))}
      {!workflows.length && (
        <div className="iam-ux-card">
          <div className="iam-ux-zero">
            <strong>No approval path yet</strong>
            <p>Add one for leave, attendance, or payroll. Requests are meant to go to the manager, then to HR.</p>
          </div>
        </div>
      )}
    </div>
  );
}

function policyCopy(policy) {
  const type = String(policy.policy_type || "").toLowerCase();
  const catalog = [
    ["device", MonitorSmartphone, "Limit which phones and computers can sign in."],
    ["ip", Globe, "Allow sign-in only from trusted networks."],
    ["location", MapPin, "Limit access to selected offices or regions."],
    ["password", KeyRound, "Set length, expiry, and reuse rules for passwords."],
    ["session", Clock, "Control how long a signed-in session stays open."],
    ["time", Timer, "Allow access only during working hours."],
  ];
  const match = catalog.find(([key]) => type.includes(key));
  return {
    Icon: match?.[1] || Shield,
    blurb: match?.[2] || "This rule is checked when someone signs in.",
    rules: Object.keys(policy.rules || {}).length,
  };
}

export function IamPoliciesTab({ policies, securitySettings }) {
  const rolePolicy = securitySettings?.mfa_policy?.role_assignment;
  return (
    <div className="iam-ux">
      <header className="iam-ux-head">
        <div>
          <h2>Access Policies</h2>
          <p>Sign-in rules for the whole organisation. A role still decides what a person can do after they are in.</p>
        </div>
        <span className="iam-ux-count">{policies.length} {policies.length === 1 ? "policy" : "policies"}</span>
      </header>
      {rolePolicy && (
        <section className="iam-ux-card pad">
          <h3>Who can change roles</h3>
          <p className="iam-ux-note">
            {rolePolicy.description || "Only these roles can assign or change access from the Users page."}
          </p>
          <p className="iam-ux-note">
            Allowed: <strong>{(rolePolicy.allowed_roles || ["admin"]).join(", ")}</strong>
          </p>
        </section>
      )}
      <div className="iam-ux-policy-grid">
        {policies.map((policy) => {
          const meta = policyCopy(policy);
          const Icon = meta.Icon;
          const active = policy.is_active !== false;
          return (
            <article key={policy.policy_id} className="iam-ux-policy">
              <div className="iam-ux-policy-icon"><Icon size={18} /></div>
              <span className="iam-ux-kicker">{String(policy.policy_type || "").replace(/_/g, " ")}</span>
              <strong>{policy.name}</strong>
              <p>{meta.blurb}</p>
              <div>
                <span>{meta.rules} {meta.rules === 1 ? "rule" : "rules"}</span>
                <span className={`iam-ux-status ${active ? "on" : "off"}`}><i />{active ? "Active" : "Inactive"}</span>
              </div>
            </article>
          );
        })}
      </div>
      {!policies.length && (
        <div className="iam-ux-card">
          <div className="iam-ux-zero">
            <strong>No access policies yet</strong>
            <p>Password, session, and device rules will appear here once they are set up for this organisation.</p>
          </div>
        </div>
      )}
    </div>
  );
}

export function IamSessionsTab({ sessions, onRevoke }) {
  return (
    <div className="iam-ux">
      <header className="iam-ux-head">
        <div>
          <h2>Session Management</h2>
          <p>Open sign-ins for this organisation. Revoke one to sign that device out.</p>
        </div>
        <span className="iam-ux-count">{sessions.length} active</span>
      </header>
      <div className="iam-ux-card">
        {sessions.length ? (
          <table className="iam-ux-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Device</th>
                <th>Browser</th>
                <th>IP</th>
                <th>Location</th>
                <th>Login</th>
                <th>Last activity</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((session) => (
                <tr key={session.session_id}>
                  <td>
                    <strong>{session.user_name || session.user_email || "—"}</strong>
                    {session.user_name && session.user_email ? <small className="iam-ux-sub">{session.user_email}</small> : null}
                  </td>
                  <td>{session.device || "—"}</td>
                  <td>{session.browser || "—"}</td>
                  <td className="iam-ux-mono">{session.ip_address || "—"}</td>
                  <td>{session.location || "—"}</td>
                  <td className="iam-ux-when">{session.login_at ? new Date(session.login_at).toLocaleString() : "—"}</td>
                  <td className="iam-ux-when">{session.last_activity_at ? new Date(session.last_activity_at).toLocaleString() : "—"}</td>
                  <td>
                    <button type="button" className="iam-ux-edit danger" onClick={() => onRevoke(session.session_id)}>Revoke</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="iam-ux-zero">
            <strong>No open sessions</strong>
            <p>A row appears here after someone signs in. You can revoke it to sign that device out.</p>
          </div>
        )}
      </div>
    </div>
  );
}

const AUDIT_LABELS = {
  roles_assigned: "Role assigned",
  role_access_updated: "Role permissions updated",
  role_created: "Role created",
  role_updated: "Role updated",
  user_invited: "User invited",
  iam_seed: "Standard roles added",
  bulk_activate: "User activated",
  bulk_deactivate: "User deactivated",
  bulk_suspend: "User suspended",
  bulk_delete: "User removed",
  session_revoked: "Session revoked",
};

function auditLabel(action) {
  const key = String(action || "").toLowerCase();
  if (AUDIT_LABELS[key]) return AUDIT_LABELS[key];
  const words = key.replace(/_/g, " ").trim();
  return words ? words.replace(/\b\w/g, (char) => char.toUpperCase()) : "Change";
}

function auditKind(action) {
  const key = String(action || "").toLowerCase();
  if (key.includes("role")) return "roles";
  if (key.includes("user") || key.includes("invite") || key.includes("session")) return "users";
  return "other";
}

function auditWhen(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const time = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const today = new Date();
  if (date.toDateString() === today.toDateString()) return `Today, ${time}`;
  return `${date.toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" })}, ${time}`;
}

export function IamAuditTab({ logs }) {
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState("all");
  const q = query.trim().toLowerCase();
  const visible = logs.filter((log) => {
    if (kind !== "all" && auditKind(log.action) !== kind) return false;
    if (!q) return true;
    return [auditLabel(log.action), log.action, log.actor_email, log.target_email, log.module_key, log.ip_address]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(q));
  });

  return (
    <div className="iam-ux">
      <header className="iam-ux-head">
        <div>
          <h2>Audit Logs</h2>
          <p>Who changed access, who it affected, and when it happened.</p>
        </div>
        <span className="iam-ux-count">{visible.length} {visible.length === 1 ? "entry" : "entries"}</span>
      </header>
      <div className="iam-audit-filters">
        {[
          ["all", "All"],
          ["roles", "Roles"],
          ["users", "Users"],
          ["other", "Other"],
        ].map(([id, label]) => (
          <button key={id} type="button" className={kind === id ? "on" : ""} onClick={() => setKind(id)}>
            {label}
          </button>
        ))}
      </div>
      <label className="iam-ux-search iam-audit-search">
        <Search size={16} />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by person, email, or action" />
      </label>
      <div className="iam-audit">
        {visible.map((log) => {
          const tone = auditKind(log.action);
          return (
            <article key={log.audit_id} className={`iam-audit-item ${tone}`}>
              <span className="iam-audit-rail" />
              <div className="iam-audit-card">
                <div className="iam-audit-top">
                  <strong>{auditLabel(log.action)}</strong>
                  <time>{auditWhen(log.created_at)}</time>
                </div>
                <p>
                  <b>{log.actor_email || "System"}</b>
                  <em>{log.target_email ? "changed" : "recorded"}</em>
                  {log.target_email ? <b>{log.target_email}</b> : null}
                </p>
                <div className="iam-audit-meta">
                  {log.module_key ? <span>{log.module_key === "iam" ? "Access" : log.module_key}</span> : null}
                  {log.ip_address ? <span>IP {log.ip_address}</span> : null}
                </div>
              </div>
            </article>
          );
        })}
        {!logs.length && (
          <div className="iam-ux-card">
            <div className="iam-ux-zero">
              <strong>No audit entries yet</strong>
              <p>Creating a user, changing a role, or revoking a session will leave a record here.</p>
            </div>
          </div>
        )}
        {!!logs.length && !visible.length && <p className="iam-ux-empty">No entries match this search.</p>}
      </div>
    </div>
  );
}
